import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from './ui/button';

export const GalleryLightbox = ({ images, initialIndex, isOpen, onClose, onBookService }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex || 0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, goToPrevious, goToNext]);

  if (!isOpen || !images.length) return null;

  const currentImage = images[currentIndex];

  return (
    <div 
      className="lightbox-overlay" 
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <div 
        className="lightbox-content relative w-[92vw] sm:w-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:-top-12 md:right-0 text-white/80 hover:text-white transition-colors"
          data-testid="lightbox-close"
        >
          <X className="w-8 h-8" />
        </button>

        {/* Navigation */}
        <button
          onClick={goToPrevious}
          className="absolute left-3 md:left-0 top-1/2 -translate-y-1/2 md:-translate-x-16 text-white/60 hover:text-white transition-colors"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-9 h-9 md:w-10 md:h-10" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-3 md:right-0 top-1/2 -translate-y-1/2 md:translate-x-16 text-white/60 hover:text-white transition-colors"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-9 h-9 md:w-10 md:h-10" />
        </button>

        {/* Image */}
        <img
          src={currentImage.url}
          alt={currentImage.title || 'Gallery image'}
          className="animate-fade-in"
        />

        {/* Image info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-black/80 to-transparent rounded-b-lg">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="text-white">
              <h3 className="font-heading text-xl">{currentImage.title}</h3>
              {currentImage.specialist && (
                <p className="text-white/70 text-sm mt-1">Por {currentImage.specialist}</p>
              )}
              {currentImage.category && (
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-xs">
                  {currentImage.category}
                </span>
              )}
            </div>
            {onBookService && (
              <Button
                onClick={() => onBookService(currentImage)}
                className="bg-copper hover:bg-copper-dark text-cream px-6 py-2 rounded-full font-medium flex items-center gap-2 w-full sm:w-auto"
                data-testid="book-this-look"
              >
                <Calendar className="w-4 h-4" />
                Reservar este look
              </Button>
            )}
          </div>
        </div>

        {/* Indicators */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? 'bg-white' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GalleryLightbox;
